'use client';

import { HeaderNotifications as PackageHeaderNotifications } from '@uicwebmaster/user-notifications/components';
import { useDrupalCollection } from '@/hooks/useDrupalCollection';
import { Button } from '@/components/ui/Button';
import Prose from '@/components/ui/Prose';
import { useUser } from '@/contexts/UserContext';

// Parse application names from environment variable (comma-separated)
// These match the field_app_name values in Drupal Application nodes
const notificationAppNames = process.env.NEXT_PUBLIC_NOTIFICATION_APP_NAMES
  ?.split(',')
  .map((name) => name.trim())
  .filter(Boolean) || [];

export function HeaderNotifications() {
  const user = useUser();

  // Only render if user is logged in with an email
  if (!user.email) return null;

  // Build tokens for token replacement in notification content
  // Supports both {{key}} and [key] syntax
  const tokens: Record<string, string> = {};

  // {{...}} syntax tokens (simple keys)
  if (user.civility) tokens['civility'] = user.civility;
  if (user.name) tokens['name'] = user.name;
  if (user.firstName) tokens['first_name'] = user.firstName;
  if (user.familyName) tokens['family_name'] = user.familyName;
  if (user.workCountry) tokens['country'] = user.workCountry;
  if (user.email) tokens['email'] = user.email;
  if (user.emailAlternatif) tokens['email_alt'] = user.emailAlternatif;
  if (user.positionTitle) tokens['position'] = user.positionTitle;
  if (user.departmentService) tokens['department_service'] = user.departmentService;
  if (user.telephone) tokens['phone'] = user.telephone;
  if (user.mobilePhone) tokens['mobile'] = user.mobilePhone;
  if (user.companyName) tokens['company_name'] = user.companyName;
  if (user.companyCode) tokens['company_code'] = user.companyCode;
  if (user.membership) tokens['membership'] = user.membership;
  // Arrays joined as comma-separated strings
  if (user.interests.length > 0) tokens['themes'] = user.interests.join(', ');
  if (user.requestedGroups.length > 0) tokens['requested_groups'] = user.requestedGroups.join(', ');
  if (user.activeGroups.length > 0) tokens['active_groups'] = user.activeGroups.join(', ');

  // [key] syntax tokens (Drupal-style) - kept for backward compatibility
  if (user.firstName) tokens['[contact:first_name]'] = user.firstName;
  if (user.familyName) tokens['[contact:last_name]'] = user.familyName;
  if (user.name) tokens['[contact:full_name]'] = user.name;
  if (user.email) tokens['[contact:email]'] = user.email;
  if (user.companyName) tokens['[company:name]'] = user.companyName;
  if (user.companyCode) tokens['[company:code]'] = user.companyCode;
  if (user.membership) tokens['[company:membership_label]'] = user.membership;

  // If NEXT_PUBLIC_NOTIFICATION_APP_NAMES is set, use simple mode (filter by app name only)
  // Otherwise, use complex mode (membership + assembly + applications)
  const useSimpleMode = notificationAppNames.length > 0;

  return (
    <PackageHeaderNotifications
      userEmail={user.email}
      userContext={{
        identity: {
          firstName: user.firstName,
          familyName: user.familyName,
          email: user.email,
          name: user.name,
        },
        membership: {
          companyName: user.companyName,
          companyCode: user.companyCode,
          membershipLabel: user.membership,
        },
      }}
      // Simple mode: filter by app names from env var (uses field_app_name)
      applicationName={useSimpleMode ? notificationAppNames : undefined}
      // Complex mode props (ignored when applicationName is set)
      membershipTaxonomyName={useSimpleMode ? undefined : user.membershipTaxonomyName}
      assemblyIds={useSimpleMode ? undefined : user.activeGroups}
      applicationIds={useSimpleMode ? undefined : user.activeApplications}
      tokens={tokens}
      maxNotifications={5}
      useDrupalCollection={useDrupalCollection}
      ui={{ Button, Prose }}
    />
  );
}
